import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Btn, StatusBadge, Empty } from "@/components/kit";
import { Building2, FileText, CheckCircle2, CalendarClock, Sparkles, TrendingUp } from "lucide-react";

const KPI = ({ icon: Icon, label, value, tint }) => (
  <Card className="flex items-center gap-4 hover:-translate-y-0.5 transition-transform" data-testid={`kpi-${label.toLowerCase()}`}>
    <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${tint}`}><Icon size={22} /></div>
    <div>
      <div className="text-2xl font-extrabold font-display text-slate-900">{value}</div>
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
    </div>
  </Card>
);

export default function Dashboard() {
  const { activeOrgId } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    if (!activeOrgId) return;
    client.get("/dashboard").then((r) => setD(r.data)).catch(() => setD(null));
  }, [activeOrgId]);

  return (
    <div>
      <PageHeader title="Painel" subtitle="Visão geral do seu conteúdo em todas as marcas."
        actions={<Btn data-testid="dashboard-new-campaign" onClick={() => navigate("/app/campanhas")}><Sparkles size={16} /> Nova campanha</Btn>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPI icon={Building2} label="Empresas" value={d?.brands ?? 0} tint="bg-blue-50 text-[#3457d5]" />
        <KPI icon={FileText} label="Conteúdos" value={d?.total ?? 0} tint="bg-indigo-50 text-indigo-600" />
        <KPI icon={CheckCircle2} label="Aprovados" value={d?.approved ?? 0} tint="bg-emerald-50 text-emerald-600" />
        <KPI icon={CalendarClock} label="Agendados" value={d?.scheduled ?? 0} tint="bg-amber-50 text-amber-600" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <h2 className="font-display text-lg font-bold text-slate-900 mb-4">Próximas publicações</h2>
          {d?.upcoming?.length ? (
            <div className="divide-y divide-slate-100">
              {d.upcoming.map((p) => (
                <div key={p.id} className="py-3 flex items-center justify-between gap-3" data-testid={`upcoming-${p.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">Dia {p.day} · {p.channel}</div>
                    <div className="text-xs text-slate-500 truncate">{p.title}</div>
                  </div>
                  <StatusBadge status={p.status} />
                </div>
              ))}
            </div>
          ) : <Empty>Crie sua primeira campanha para ver publicações aqui.</Empty>}
        </Card>

        <Card>
          <h2 className="font-display text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><TrendingUp size={18} className="text-[#3457d5]" /> Resumo</h2>
          <ul className="space-y-3 text-sm">
            <li className="flex justify-between"><span className="text-slate-500">Publicados (Sandbox)</span><b>{d?.published ?? 0}</b></li>
            <li className="flex justify-between"><span className="text-slate-500">Pendentes de aprovação</span><b>{d?.pending ?? 0}</b></li>
            <li className="flex justify-between"><span className="text-slate-500">Total gerado</span><b>{d?.total ?? 0}</b></li>
          </ul>
          <div className="mt-5 rounded-xl bg-sky-50 border border-sky-100 p-3 text-xs text-sky-700">
            Ambiente <b>SANDBOX</b> ativo. Publicações não são enviadas a redes reais até a conexão OAuth oficial.
          </div>
          <Btn variant="secondary" className="w-full mt-4" onClick={() => navigate("/app/campanhas")}>Criar campanha com IA</Btn>
        </Card>
      </div>
    </div>
  );
}
